/* jshint esversion: 11 */
document.addEventListener("alpine:initialized", function () {
  const FOCUS_TRAP = `
    <span
      aria-hidden='true'
      class='modal-dialog__focus-trap'
      tabindex='0'
    ></span>
  `;

  const ACTIVE = "active";
  const ANIMATED = "animated";
  const ANIMATION_DURATION = 250;
  const ARIA_LABEL = "aria-label";
  const BLOCK = "block";
  const CLICK = "click";
  const CLOSE = "close";
  const CLOSE_TITLE = "Close";
  const DATA_HIDE = "data-cta-modal-hide";
  const DATA_SHOW = "data-cta-modal-show";
  const EMPTY_STRING = "";
  const ENTER = "enter";
  const ESCAPE = "escape";
  const FALSE = "false";
  const FOCUSIN = "focusin";
  const FUNCTION = "function";
  const HIDDEN = "hidden";
  const KEYDOWN = "keydown";
  const MODAL_LABEL_FALLBACK = "modal";
  const NONE = "none";
  const PREFERS_REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
  const SPACE = " ";
  const SPACE_REGEX = /\s+/g;
  const STATIC = "static";
  const TAB = "tab";
  const TEMPLATE = "template";
  const TRUE = "true";

  const FOCUSABLE_SELECTORS = [
    "[contenteditable]",
    '[tabindex="0"]:not([disabled])',
    "a[href]",
    "audio[controls]",
    "button:not([disabled])",
    "iframe",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "summary",
    "textarea:not([disabled])",
    "video[controls]"
  ].join(",");

  type AlpineState = {
    name: any;
    status: boolean;
    [key: string]: any;
  };

  const modalTemplate = document.createElement("template");
  modalTemplate.innerHTML = `
    <style>.modal-dialog{z-index:1000;position:fixed;top:0;bottom:0;left:0;right:0;overflow-y:auto}.modal-dialog .modal-dialog__overlay{background-color:rgba(0,0,0,var(--tw-bg-opacity));--tw-bg-opacity:.5;position:fixed;top:0;bottom:0;left:0;right:0}.modal-dialog .modal-dialog__panel{min-height:calc(100vh - 3em);z-index:10;justify-content:center;align-items:center;margin:1.5em;display:flex;position:relative}.modal-dialog .modal-dialog__contents{width:100%;max-width:42rem;--tw-bg-opacity:1;background-color:rgba(255,255,255,var(--tw-bg-opacity));--tw-shadow:0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -4px rgba(0,0,0,.1);--tw-shadow-colored:0 10px 15px -3px var(--tw-shadow-color),0 4px 6px -4px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow,0 0 transparent),var(--tw-ring-shadow,0 0 transparent),var(--tw-shadow);border-radius:.25rem;padding:3rem;position:relative;overflow-y:auto}.modal-dialog .modal-dialog__contents:focus{border:5px solid red!important}.modal-dialog__close{z-index:20;cursor:pointer;transition:background-color .3s;position:fixed;top:1rem;right:1rem}.modal-dialog .modal-dialog__focus-trap{opacity:0;width:0;height:0;position:fixed;top:0;left:0;overflow:hidden} </style>
    <div style="display: none;" 
        x-show="status === true"
        x-init="$watch('status', (newV, oldV) => { newV && setFocus(newV) })"
        x-on:keydown.escape.prevent.stop.window="status = false"
        role="dialog"
        tabindex="-1"
        aria-modal="true"
        :aria-hidden="!status"
        x-id="['modal-title']"
        :aria-labelledby="$id('modal-title')"
        class="modal-dialog"
        @modal-dialog.window="open" style="display: none;">
        
        ${FOCUS_TRAP}
        
        <!-- Overlay -->
        <div x-transition.opacity class="modal-dialog__overlay" x-on:click="status = false"></div>
 
        <!-- Close button -->
        <x-close-button aria-label="Close Modal Window" class="modal-dialog__close" x-on:click.stop.prevent="status = false"></x-close-button>
 
        <!-- Panel -->
        <div @click="status = false" x-transition class="modal-dialog__panel" tabindex="-1">
            <div class="modal-dialog__contents" x-on:click.stop>
                <slot></slot>
            </div>
        </div>
        
        ${FOCUS_TRAP}
    </div>`;

  class ModalDialog extends HTMLElement {
    state: AlpineState;
    _modal: HTMLElement;
    _modalOverlay: HTMLElement;
    focusableElements: any;
    first: any;
    last: any;

    _isActive = false;
    _activeElement: HTMLElement | null = null;
    _focusTrapList: NodeListOf<HTMLElement>;

    constructor() {
      super();

      this._handleFocusIn = this._handleFocusIn.bind(this);
      this._handleKeyDown = this._handleKeyDown.bind(this);

      this.state = Alpine.reactive({
        name: null,
        status: false,
        open: this.open.bind(this),
        setFocus: this.setFocus.bind(this)
      });

      const shadow = this.attachShadow({ mode: "open" });
      shadow.appendChild(modalTemplate.content.cloneNode(true));

      Alpine.addScopeToNode(shadow, this.state);
      Alpine.initTree(shadow);

      this._focusTrapList = shadow.querySelectorAll(".modal-dialog__focus-trap");
      this._modal = shadow.querySelector(".modal-dialog__panel") as HTMLElement;
      this._modalOverlay = shadow.querySelector(".modal-dialog__overlay") as HTMLElement;
    }

    connectedCallback() {
      this._addEvents();
    }

    /**
     * Sets the body of our modal
     * We are setting it in the light DOM as not to mix with the component
     * @param contentNode {Element}
     * @return {ModalDialog}
     */
    setContents(contentNode: Element) {
      this.innerHTML = "";
      this.appendChild(contentNode);
      return this;
    }

    /**
     * Adds content to the body of our modal
     * @param contentNode {Element}
     * @return {ModalDialog}
     */
    addContents(contentNode: Element) {
      this.appendChild(contentNode);
      return this;
    }

    setFocus(v: any) {
      this._isActive = v;

      const activeElement = document.activeElement as HTMLElement;

      // Cache active element?
      if (this._isActive && activeElement) {
        this._activeElement = activeElement;
      }

      this._focusModal();
    }

    _addEvents() {
      // Prevent doubles.
      this._removeEvents();

      document.addEventListener(FOCUSIN, this._handleFocusIn);
      document.addEventListener(KEYDOWN, this._handleKeyDown);
    }

    // ======================
    // Helper: remove events.
    // ======================

    _removeEvents() {
      document.removeEventListener(FOCUSIN, this._handleFocusIn);
      document.removeEventListener(KEYDOWN, this._handleKeyDown);
    }

    // =========================
    // Event: focus in document.
    // =========================

    _handleFocusIn() {
      // Early exit.
      if (!this._isActive) {
        return;
      }

      // prettier-ignore
      const activeElement = (
                // Get active element.
                this.shadowRoot.activeElement ||
                document.activeElement
            ) as HTMLElement;

      // Get booleans.
      const isFocusTrap1 = activeElement === this._focusTrapList[0];
      const isFocusTrap2 = activeElement === this._focusTrapList[1];

      // Set later.
      let focusListReal: HTMLElement[] = [];

      focusListReal = Array.from(this.querySelectorAll(FOCUSABLE_SELECTORS)) as HTMLElement[];

      // Get "shadow" elements.
      const focusListShadow = Array.from(this._modal.querySelectorAll(FOCUSABLE_SELECTORS)) as HTMLElement[];

      // Get "total" elements.
      const focusListTotal = focusListShadow.concat(focusListReal);

      // Get first & last items.
      const focusItemFirst = focusListTotal[0];
      const focusItemLast = focusListTotal[focusListTotal.length - 1];

      // Focus trap: above?
      if (isFocusTrap1 && focusItemLast) {
        this._focusElement(focusItemLast);

        // Focus trap: below?
      } else if (isFocusTrap2 && focusItemFirst) {
        this._focusElement(focusItemFirst);
      } else if (this._isOutsideModal(activeElement)) {
        this._focusModal();
      }
    }

    // =================
    // Event: key press.
    // =================

    _handleKeyDown({ key }: KeyboardEvent) {
      // Get key.
      key = key.toLowerCase();

      // Tab key?
      if (key === TAB) {
        this._handleFocusIn();
      }
    }

    _focusElement(element: HTMLElement) {
      window.requestAnimationFrame(() => {
        if (typeof element.focus === FUNCTION) {
          element.focus();
        }
      });
    }

    // ====================
    // Helper: focus modal.
    // ====================

    _focusModal() {
      setTimeout(() => {
        this._modal.focus();
      }, 500);
    }

    // =============================
    // Helper: detect outside modal.
    // =============================

    _isOutsideModal(element?: HTMLElement) {
      // Early exit.
      if (!this._isActive || !element) {
        return false;
      }

      // Has element?
      const hasElement = this.contains(element) || this._modal.contains(element);

      // Get boolean.
      const bool = !hasElement;

      // Expose boolean.
      return bool;
    }

    open(event: any) {
      if (event && event.detail && this.state.name && this.state.name === event.detail) {
        this.state.status = true;
      }
    }

    attributeChangedCallback(name: string, oldValue: any, newValue: any) {
      if (name === "name") {
        this.state.name = newValue;
      }
    }

    static get observedAttributes() {
      return ["name"];
    }

    disconnectedCallback() {
      // Clean up event listener and MutationObserver
      this.removeEventListener("keydown", this._handleKeyDown);
    }
  }

  (function (registry) {
    if (!registry.get("alpine-modal-dialog")) {
      registry.define("alpine-modal-dialog", ModalDialog);
    }
  })(window.customElements);
});
